import React, { useState } from "react";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import {
  TimelineServiceBar,
  TimelineServiceLane,
} from "./serviceLanes";
import {
  clampPct,
  formatTimelineDate,
  TimelineWindow,
  xPct,
} from "./timeScale";
import TimelineTooltip, { TooltipEntry } from "./TimelineTooltip";
import { COMPACT_ROW_HEIGHT } from "./TestsRow";
import classes from "./Timeline.module.css";

type Props = {
  lane: TimelineServiceLane;
  window: TimelineWindow;
  /** Events & Services view: a header row plus one row per service. */
  expanded?: boolean;
};

const LANE_MIN_HEIGHT = 46;
const LANE_BAR_HEIGHT = 4;
const LANE_ROW_STEP = 10;
const LANE_PAD = 12;
const EXPANDED_BAR_HEIGHT = 8;

const barSpan = (bar: TimelineServiceBar, window: TimelineWindow) => {
  const left = clampPct(xPct(bar.startMs, window));
  const right = clampPct(xPct(bar.endMs, window));
  return { left, width: Math.max(right - left, 0) };
};

const barTooltip = (bar: TimelineServiceBar) => [
  bar.laneLabel,
  `${formatTimelineDate(bar.startMs)} → ${formatTimelineDate(bar.endMs)}`,
];

/** One bar, positioned on the shared scale; at least 4px so a one-day
 *  service stays visible in a month. */
const Bar = ({
  bar,
  window,
  top,
  height,
  hovered,
  onHover,
}: {
  bar: TimelineServiceBar;
  window: TimelineWindow;
  top: number;
  height: number;
  hovered: boolean;
  onHover: (id: string | null) => void;
}) => {
  const theme: Theme = useTheme();
  const { left, width } = barSpan(bar, window);
  return (
    <div
      style={{ ...styles.barHitArea, left: `${left}%`, width: `${width}%`, top: top - 3 }}
      onMouseEnter={() => onHover(bar.id)}
      onMouseLeave={() => onHover(null)}
    >
      <div style={styles.bar(theme, hovered, height)} />
    </div>
  );
};

/**
 * A service category's swimlane: every prescribed service of that kind as a
 * start → end bar. Overlapping services stack into sub-rows (`packBars`).
 */
const ServiceLane = ({ lane, window, expanded = false }: Props) => {
  const theme: Theme = useTheme();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const hovered = lane.bars.find((b) => b.id === hoveredId) ?? null;

  if (expanded) {
    return (
      <div style={styles.section}>
        {/* The space above each section lives in the header row, so the
            divider runs unbroken between sections. */}
        <div className={classes.row} style={{ height: 42 }}>
          <div
            className={`${classes.label} ${classes.labelInline} ${classes.labelCompact}`}
            style={styles.sectionHeaderLabel}
          >
            <div style={styles.laneName(theme)}>{lane.label}</div>
            <div style={styles.count(theme)}>{lane.bars.length}</div>
          </div>
          {/* Empty, but still the plot column: its left border is the
              vertical divider, which must not break at section headers. */}
          <div className={classes.plot} />
        </div>
        {/* One row per prescribed service, repeats included — each keeps its
            own timeline. Packed order, so rows follow start dates. */}
        {lane.bars.map((bar) => {
          const isHovered = bar.id === hoveredId;
          const span = barSpan(bar, window);
          return (
            <div key={bar.id} className={classes.row} style={{ height: COMPACT_ROW_HEIGHT }}>
              <div
                className={`${classes.label} ${classes.labelCompact}`}
                style={styles.highlight(theme, isHovered)}
              >
                <div style={styles.serviceName(theme)}>{bar.name}</div>
              </div>
              <div className={classes.plot}>
                <div
                  className={classes.baseline}
                  style={{ backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK }}
                />
                <Bar
                  bar={bar}
                  window={window}
                  top={(COMPACT_ROW_HEIGHT - EXPANDED_BAR_HEIGHT) / 2}
                  height={EXPANDED_BAR_HEIGHT}
                  hovered={isHovered}
                  onHover={setHoveredId}
                />
                {isHovered && (
                  <TimelineTooltip
                    leftPct={span.left + span.width / 2}
                    anchorY={(COMPACT_ROW_HEIGHT - EXPANDED_BAR_HEIGHT) / 2}
                  >
                    <TooltipEntry title={bar.name} lines={barTooltip(bar)} />
                  </TimelineTooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const height = Math.max(
    LANE_MIN_HEIGHT,
    LANE_PAD * 2 + (lane.rowCount - 1) * LANE_ROW_STEP + LANE_BAR_HEIGHT
  );
  const hoveredSpan = hovered ? barSpan(hovered, window) : null;

  return (
    <div className={`${classes.row} ${classes.rowDivider}`} style={{ height }}>
      <div
        className={`${classes.label} ${classes.labelInline}`}
        style={styles.highlight(theme, Boolean(hovered))}
      >
        <div style={styles.laneName(theme)}>{lane.label}</div>
        <div style={styles.count(theme)}>{lane.bars.length}</div>
      </div>
      <div className={classes.plot}>
        {lane.bars.map((bar) => (
          <Bar
            key={bar.id}
            bar={bar}
            window={window}
            top={LANE_PAD + bar.row * LANE_ROW_STEP}
            height={LANE_BAR_HEIGHT}
            hovered={bar.id === hoveredId}
            onHover={setHoveredId}
          />
        ))}
        {hovered && hoveredSpan && (
          <TimelineTooltip
            leftPct={hoveredSpan.left + hoveredSpan.width / 2}
            anchorY={LANE_PAD + hovered.row * LANE_ROW_STEP}
          >
            <TooltipEntry title={hovered.name} lines={barTooltip(hovered)} />
          </TimelineTooltip>
        )}
      </div>
    </div>
  );
};

export default ServiceLane;

const styles = {
  section: {
    display: "flex",
    flexDirection: "column" as "column",
  },
  // Text sits at the bottom of the taller header row; the row itself still
  // stretches so its plot column (the divider) keeps full height.
  sectionHeaderLabel: {
    alignItems: "flex-end",
    paddingBottom: "6px",
  },
  highlight: (theme: Theme, highlighted: boolean) => ({
    backgroundColor: highlighted ? theme.colorTokens.P[10] : "transparent",
  }),
  laneName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as "nowrap",
  }),
  serviceName: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as "nowrap",
  }),
  count: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    flexShrink: 0,
  }),
  barHitArea: {
    position: "absolute" as "absolute",
    minWidth: "4px",
    padding: "3px 0",
    zIndex: 2,
  },
  // Figma 16450:62433: the fill stays P[90] on hover and gains a 1px P[60]
  // outline with a soft P[50] glow; border-box keeps the bar's thickness.
  bar: (theme: Theme, hovered: boolean, height: number) => ({
    height: `${height}px`,
    boxSizing: "border-box" as "border-box",
    borderRadius: "999px",
    backgroundColor: theme.colorTokens.P[90],
    border: `1px solid ${hovered ? theme.colorTokens.P[60] : "transparent"}`,
    boxShadow: hovered ? `0 1px 4px 0 ${theme.colorTokens.P[50]}` : "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    minWidth: "4px",
  }),
};
